import { useState, useEffect, useRef } from "react";

interface UseSearchHighlightOptions<T> {
  searchQuery?: string | undefined;
  items: T[];
  isCurrentSearchResult?: boolean | undefined;
  currentOccurrenceIndex?: number | undefined;
  getTextFromItem: (item: T) => string | undefined;
}

interface UseSearchHighlightReturn {
  highlightedIndices: number[];
  currentHighlightIndex: number;
  itemRefs: React.MutableRefObject<(HTMLElement | null)[]>;
  getHighlightClass: (idx: number) => string;
}

export const useSearchHighlight = <T>({
  searchQuery,
  items,
  isCurrentSearchResult = false,
  currentOccurrenceIndex,
  getTextFromItem,
}: UseSearchHighlightOptions<T>): UseSearchHighlightReturn => {
  const [highlightedIndices, setHighlightedIndices] = useState<number[]>([]);
  const [currentHighlightIndex, setCurrentHighlightIndex] =
    useState<number>(-1);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (!searchQuery || !items.length) {
      setHighlightedIndices([]);
      setCurrentHighlightIndex(-1);
      return;
    }

    const query = searchQuery.toLowerCase();
    const matchingIndices: number[] = [];
    let occurrenceIndex = 0;

    items.forEach((item, index) => {
      const text = getTextFromItem(item);
      if (text && text.toLowerCase().includes(query)) {
        matchingIndices.push(index);
        if (
          isCurrentSearchResult &&
          currentOccurrenceIndex === occurrenceIndex
        ) {
          setCurrentHighlightIndex(index);
        }
        occurrenceIndex++;
      }
    });

    setHighlightedIndices(matchingIndices);

    if (
      isCurrentSearchResult &&
      currentOccurrenceIndex !== undefined &&
      matchingIndices.length > 0
    ) {
      const targetIndex = matchingIndices[currentOccurrenceIndex];
      if (targetIndex !== undefined && itemRefs.current[targetIndex]) {
        const timeoutId = setTimeout(() => {
          itemRefs.current[targetIndex]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    searchQuery,
    items,
    isCurrentSearchResult,
    currentOccurrenceIndex,
    getTextFromItem,
  ]);

  const getHighlightClass = (_idx: number): string => {
    return "";
  };

  return {
    highlightedIndices,
    currentHighlightIndex,
    itemRefs,
    getHighlightClass,
  };
};
